import React from "react";
import "../DashboardStyles.css";

const METHODS = [
  ["Regional community survey (online + paper)", "203 completed responses; all eleven counties represented", "Mar–May 2026"],
  ["Western Nebraska Teen Opportunities Survey", "3,826 students; 22 school districts", "Fall 2023"],
  ["CEDS Strategy Council recruitment campaign", "7 applications received; Council seated", "Spring 2026"],
  ["CEDS kickoff meeting (hybrid, Teams)", "Convened; 2 Council members attended in person/online", "June 16, 2026"],
  ["Targeted partner outreach (see 11.5)", "Hospitals, colleges, schools, chambers, local govts, partnerships", "2025–2026"],
  ["CEDS Council Portal (async LMS)", "Discussions, document review, and drop-box participation", "Ongoing"],
];

const ISSUES = [
  ["Finding affordable housing to rent or buy", "82%", "51%"],
  ["Keeping local businesses open", "82%", "49%"],
  ["Young people leaving the area", "82%", "58%"],
  ["Finding a good job with decent pay", "74%", "37%"],
  ["Finding reliable childcare", "51%", "32%"],
  ["Access to job training or education", "39%", "11%"],
  ["Water or sewer system quality", "35%", "14%"],
  ["Access to fast, reliable internet", "19%", "4%"],
];

const TEENS = [
  ["Likely to stay after high school", "23% likely / 41% unlikely", "Goal 2 — retention & return"],
  ["Can picture living in the area in future", "49%", "Goal 2; Goal 1 quality of life"],
  ["Top reasons they would stay", "Family ties (49%), good place to raise a family (47%), lower cost of living (32%)", "Quality-of-life messaging (Ch. 6)"],
  ["Top reasons they would leave", "More money elsewhere (60%), career opportunity elsewhere (58%), lack of things to do (54%)", "Goal 2 wages; Goal 1 amenities"],
  ["Want to own a business someday", "52%", "Goal 2 — succession & entrepreneurship"],
  ["Want a career internship", "54%", "Goal 2 — work-based learning pipeline"],
  ["Plan to attend college / trade school", "65%", "Ch. 7 — CSC & WNCC pipeline"],
  ["Top career interests", "Health science (26%), agriculture (22%), business (19%), skilled trades (17%)", "Goal 2 target sectors"],
  ["Have ever been asked for input by adults", "17%", "Goal 4 — civic engagement gap"],
  ["Would volunteer if asked by a community leader", "66% (75% at smaller schools)", "Goal 4 — latent civic capacity"],
];

const INPUT_TO_RESPONSE = [
  ["Housing is the #1 felt constraint", "Survey (82%); Council (5 of 7)", "Goal 1, Obj 1.1 — regional housing asset inventory + Year 2 regional development meetings"],
  ["Childcare is economic infrastructure", "Survey (53% affected); Council (4 of 7)", "Goal 1, Obj 1.2 — childcare mapping & employer models"],
  ["Young people are leaving", "Survey (58% serious); Teens (41% unlikely to stay)", "Goal 2 — retention, return & work-based learning"],
  ["Smaller communities feel unseen", "Council", "Geographic-equity metrics added across Goals 1 & 4 (see annotation)"],
  ["Funding info arrives too late", "Council", "NEW — Goal 4 “Panhandle Opportunity Tracker” strategy"],
  ["Tourism potential is under-used", "Council", "Goal 4, Obj 4.2 — sharpen toward agritourism & heritage tourism"],
  ["Residents want outcomes, not process", "Survey (Q18); Council (all)", "Plan built on lagging outcomes & public dashboard (Goal 4)"],
  ["PADD is largely unknown to the public", "Survey (41% never heard of PADD)", "Goal 4 — visibility & accountability emphasis"],
];

function SimpleTable({ head, rows }) {
  return (
    <div className="table-wrapper">
      <table className="ceds-table">
        <thead>
          <tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map(([first, ...rest]) => (
            <tr key={first}>
              <td><strong>{first}</strong></td>
              {rest.map((c, i) => <td key={i}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Chapter11Dashboard() {
  return (
    <div className="chapter chapter11">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 11</p>
        <h2>Public Engagement, Community Input &amp; the Planning Climate</h2>
        <p className="chapter-lede">
          A regional strategy is only as credible as the listening behind it.
          This chapter documents how the 2026–2031 CEDS was informed by the
          people of the Panhandle, what they told us, and (with equal honesty)
          the conditions under which that listening took place. It is written
          plainly, because the credibility of this plan depends on neither
          overstating our reach nor hiding its limits.
        </p>

        <h3>11.1 How This Plan Was Built</h3>
        <p>
          This CEDS draws on three streams of primary community input gathered
          during the 2026 planning cycle, layered on the secondary data analyzed
          in Chapters 2 through 8:
        </p>
        <ul className="ceds-list">
          <li>
            <strong>A regional community survey</strong> completed by{" "}
            <strong>203 residents, workers, and business owners</strong> across
            all eleven counties (March–May 2026).
          </li>
          <li>
            <strong>The Western Nebraska Teen Opportunities Survey</strong>,
            completed by <strong>3,826 students in grades 6–12 across 22
            Panhandle school districts</strong> in fall 2023 — the largest
            single dataset on the region’s rising generation.
          </li>
          <li>
            <strong>The CEDS Strategy Council</strong>, a volunteer body
            recruited from across the region to set benchmarks, validate
            findings, open doors, and carry the story — together with the
            detailed applications submitted by candidates for that Council.
          </li>
        </ul>
        <p>
          Together these sources do something the secondary data cannot: they
          tell us not only what the numbers say, but what residents experience
          and prioritize. Where community input and federal data point in the
          same direction, this plan treats the finding as firmly established.
          Where they diverge, the divergence is noted rather than smoothed over.
        </p>

        <h3>11.2 Engagement Methods and Reach</h3>
        <p>
          PADD pursued engagement through the channels available to a
          three-person regional office, prioritizing breadth of geography over
          volume of response. The table below summarizes the methods used and
          their reach.
        </p>
        <SimpleTable head={["Engagement Method", "Reach / Result", "Period"]} rows={METHODS} />

        <h3>11.3 What the Community Survey Told Us</h3>
        <p>
          The 203 survey respondents skew toward established residents:{" "}
          <strong>84% currently live in the Panhandle</strong>, and{" "}
          <strong>88% are age 35 or older</strong>, with the largest shares in
          the 35–49 (32%) and 50–64 (31%) brackets. Geographically, responses
          concentrated in Scotts Bluff County (57%) and Box Butte County (17%),
          consistent with the region’s population distribution, while every one
          of the eleven counties is represented. This profile means the survey
          speaks with particular authority about the priorities of the region’s
          working-age and established population — the people whose decisions
          to stay or leave most directly shape the Panhandle’s economic
          trajectory. Full results are available on the Survey tab of this
          dashboard.
        </p>

        <h4>Overall sentiment: rooted, but concerned</h4>
        <p>
          Residents are committed to the region but clear-eyed about its
          direction. A quarter (<strong>25%</strong>) rate the Panhandle an
          “excellent” place to live and work, and another <strong>31%</strong>{" "}
          call it “good.” Yet <strong>34%</strong> describe it as only “fair —
          with concerns about the direction things are going,” and roughly one
          in ten rate it “poor.” Commitment to place remains strong:{" "}
          <strong>72%</strong> plan to still be living or working in the
          Panhandle five years from now (39% “definitely,” 33% “probably”),
          while <strong>19% are unsure</strong> — a meaningful pool of residents
          whose retention is not guaranteed.
        </p>

        <h4>The problems residents feel most acutely</h4>
        <p>
          Asked how much of a problem each of eight issues is in their
          community, respondents identified a clear top tier. The figures below
          combine “serious” and “moderate” problem ratings, with the
          “serious”-only share shown separately.
        </p>
        <SimpleTable head={["Community Issue", "Serious + Moderate", "Serious only"]} rows={ISSUES} />
        <p className="source-note">
          Source: PADD Regional Community Survey, 2026 (N = 203). Childcare
          carried a high “don’t know” share (28%), reflecting that the issue is
          felt most acutely by households with young children.
        </p>

        <div className="callout">
          <span className="callout-label">What the ranking tells us</span>
          <p>
            The community’s top three concerns: housing, business retention, and
            youth out-migration — map directly onto Goals 1, 2, and 4 of this
            plan. Notably, <strong>young people leaving</strong> registers the
            highest “serious” rating of any issue (58%), and the teen survey in
            Section 11.4 shows that concern is well-founded. Just as notably,{" "}
            <strong>broadband ranks last</strong> (19%) — a useful corrective to
            the common rural assumption that connectivity is residents’ first
            concern. The Panhandle’s broadband investment (Goal 3) remains
            essential infrastructure, but residents experience housing and jobs
            as the binding constraints.
          </p>
        </div>

        <h4>Housing and childcare: lived, not theoretical</h4>
        <p>
          These are not abstract policy categories for Panhandle residents.{" "}
          <strong>Fifty-four percent</strong> of respondents say the lack of
          affordable housing has affected them directly (20%) or affected
          someone they know (34%). Childcare follows the same pattern among
          those it touches: <strong>53%</strong> report it has prevented them or
          someone they know from working full-time, taking a job, or starting a
          business. These lived-experience findings substantiate the “housing +
          childcare as economic infrastructure” framing at the center of Goal 1
          — this is community evidence, not planner assumption.
        </p>

        <h4>A single most-important priority</h4>
        <p>
          When forced to rank three broad directions, respondents split almost
          evenly between <strong>growing local businesses and jobs</strong> (133
          first-place rankings) and <strong>making the Panhandle a better place
          to live</strong> — housing, childcare, quality of life (128
          first-place rankings). “Making sure PADD has a clear, accountable plan
          and communicates openly” was ranked lowest by most respondents. That
          result is itself instructive: residents want outcomes, not process.
          Accountability earns its place in this plan (Goal 4) not as an end in
          itself, but as the discipline that makes the business-growth and
          quality-of-life results residents actually want more likely to
          materialize.
        </p>

        <h3>11.4 What Young People Told Us</h3>
        <p>
          The region’s most consequential long-term economic question (whether
          its young people build their futures here) is answered most directly
          by young people themselves. The 2023 Western Nebraska Teen
          Opportunities Survey reached <strong>3,826 students across 22
          districts</strong>, making it the single most authoritative window
          into the rising generation’s intentions.
        </p>
        <p>
          The headline finding validates residents’ top concern. Only 23% of
          teens say it is likely they will stay in their community after
          graduation, while 41% say it is unlikely. Looking further out, 49% can
          picture themselves living in the area in the future (a meaningful
          opening) and 29% say they would likely return after time away. The
          Panhandle’s realistic strategy is therefore not to prevent departure
          for college or first jobs, but to keep the door open for return,
          exactly as Goal 2 and Chapter 7 argue.
        </p>
        <p>
          These regional averages, like the income averages in Chapter 2.3, mask
          substantial variation by community. Banner County students reported
          far stronger retention intent than the regional average — 38 percent
          likely to stay and 64 percent picturing a future in the area, compared
          to 23 percent and 49 percent regionally — while Alliance and Morrill
          students reported the weakest retention intent, at 16 percent likely
          to stay in both districts. Smaller schools as a group consistently
          out-performed large schools on every retention measure: 25 percent
          likely to stay versus 22 percent, and 54 percent picturing a future in
          the area versus 47 percent. This pattern complicates a simple “youth
          are leaving” narrative: in at least one of the Panhandle’s smallest,
          most frontier communities, the teens surveyed are considerably more
          likely to stay or return than their peers in the region’s larger
          towns. Retention strategy should account for this variation rather
          than treat youth out-migration as a uniform regional condition.
        </p>
        <p className="source-note">
          Source note: Western Nebraska Teen Opportunities Survey, Heartland
          Center for Leadership Development with Career Connections of Western
          Nebraska / ESU 13, October 2023 (N = 3,826). District-level results,
          p. 3.
        </p>

        <SimpleTable head={["What teens said", "Finding", "CEDS connection"]} rows={TEENS} />
        <p className="source-note">
          Source: Western Nebraska Teen Opportunities Survey, Heartland Center
          for Leadership Development with Career Connections of Western
          Nebraska / ESU 13, October 2023 (N = 3,826).
        </p>

        <div className="callout">
          <span className="callout-label">The most actionable youth finding</span>
          <p>
            Only <strong>17% of teens have ever been asked</strong> what would
            make their community better — yet <strong>two-thirds say they would
            volunteer if a community leader asked them.</strong> That gap is a
            near-free opportunity. The career interests teens express (health,
            agriculture, business, trades) line up precisely with the workforce
            pipelines in Goal 2 and the programs at WNCC and Chadron State, and{" "}
            <strong>52% want to own a business someday</strong> — a direct
            feedstock for the business-succession strategy in Objective 2.1.
          </p>
        </div>

        <h3>11.5 The CEDS Strategy Council: Recruitment, Selection &amp; What We Learned</h3>
        <p>
          PADD designed the CEDS Strategy Council to be a working body (7
          members with geographic spread across the region) whose role is to set
          benchmarks, open doors, validate results, and carry the story, not to
          re-plan the region. The Council was built intentionally on diversity
          of place, background, and perspective, and structured for
          accessibility through a blended in-person and virtual format.
        </p>

        <h4>How members were recruited and selected</h4>
        <p>
          Recruitment was deliberate and wide. Over the spring 2026 cycle, PADD
          issued direct invitations and an open application (deadline May 1)
          across the full institutional landscape of the Panhandle:
        </p>
        <ul className="ceds-list">
          <li>Regional hospitals and the six Critical Access Hospitals;</li>
          <li>Chadron State College and Western Nebraska Community College;</li>
          <li>County and municipal governments and the region’s chambers of commerce;</li>
          <li>PADD’s own executive board;</li>
          <li>The regional housing conference and the 6 Regions 1 Nebraska network;</li>
          <li><strong>Every Panhandle school district</strong> — principals, coaches, and teachers;</li>
          <li>The Panhandle Partnership and the region’s major employers.</li>
        </ul>
        <p>
          Selection criteria favored geographic representation across the eleven
          counties, a mix of professional backgrounds (local government, private
          sector, nonprofit/workforce/education, and agriculture/industry), and a
          demonstrated willingness to commit to the meeting schedule. Applicants
          were asked to describe the economic challenges they see, the
          opportunities they would pursue, and what they would consider a
          successful outcome; responses that became a data source in their own
          right.
        </p>

        <h4>An honest accounting of the response</h4>
        <p>
          That broad, repeated outreach yielded <strong>seven
          applications</strong>, and <strong>two members attended the CEDS
          kickoff</strong>. PADD regards the effort as having been made in good
          faith and at full reach for a three-person office; the modest response
          is addressed candidly in Section 11.6 as a reflection of the regional
          engagement climate, not of community indifference to the issues.
          Critically, the applications that were received were substantive, and
          the analysis below treats them as genuine community input.
        </p>

        <h4>What the Council applicants told us</h4>
        <p>
          The seven applicants were unusually well-qualified — most rated
          themselves “very comfortable” with data and strategic planning, and
          all brought implementation experience rather than opinion alone.
          Their written responses converged on a clear set of themes that both
          validate and sharpen this plan:
        </p>
        <ul className="ceds-list">
          <li>
            <strong>Childcare as economic infrastructure</strong> — raised by
            four of seven applicants, framed explicitly as a workforce and
            economic-development issue “on par with housing, transportation, and
            workforce development,” not as a social service. (Validates Goal 1,
            Objective 1.2.)
          </li>
          <li>
            <strong>Housing</strong> — raised by five of seven as a constraint
            on workforce stability, with specific concern that empty,
            deteriorating properties sit unused while demand goes unmet.
            (Validates Goal 1, Objective 1.1.)
          </li>
          <li>
            <strong>Frontier representation and resource equity</strong> —
            applicants from Sheridan County and the village of McGrew pressed a
            pointed question: how does an eleven-county CEDS benefit the
            smallest communities, not just Scottsbluff? (Sharpens the
            geographic-equity emphasis in Goals 1 and 4.)
          </li>
          <li>
            <strong>Career-connected workforce pathways</strong> — one applicant
            detailed an active “high-wage, high-demand, high-skill” model
            connecting students to employers such as Aulick Industries and
            Western Sugar. (Validates Goal 2, Objective 2.2; a ready
            implementation partner.)
          </li>
          <li>
            <strong>Authentic, place-based tourism</strong> — detailed, credible
            opportunity in agritourism, heritage tourism (the Mari Sandoz
            legacy), and ranch stays, from an applicant who has hosted
            international visitors and revived a community theater into a
            creative district. (Sharpens Goal 4, Objective 4.2.)
          </li>
          <li>
            <strong>Real-time access to funding</strong> — a recurring, concrete
            barrier: “by the time I learn about a grant I qualify for, the
            deadline has already passed.” (Now addressed by a new strategy in
            Goal 4; see 11.7.)
          </li>
          <li>
            <strong>Above all, actionability</strong> — applicants asked, in
            their own words, for “something quantifiable … something that makes
            a difference,” not “theoretical discussion.”
          </li>
        </ul>

        <h3>11.6 The Planning Climate: An Honest Accounting</h3>
        <p className="chapter-lede">
          This section is included not as an excuse, but as an explanation of
          the environment in which this CEDS was produced — and as the strategic
          rationale for Goal 4. A plan that misrepresents its own conditions
          cannot be trusted to measure anything else honestly.
        </p>
        <p>
          Several conditions shaped what was achievable in this cycle, and
          naming them is the first step in addressing them:
        </p>
        <ul className="ceds-list">
          <li>
            <strong>A lapse in the planning cycle.</strong> A completed CEDS had
            not been produced in roughly five years. When a recurring process
            goes dormant, the institutional culture that sustains it (the
            habits, relationships, and shared expectations) has to be rebuilt
            rather than simply resumed.
          </li>
          <li>
            <strong>Civic engagement fatigue.</strong> The Panhandle’s small,
            overlapping pool of engaged citizens was, during this same period,
            being asked for its time by county commissioner elections, county
            strategic planning sessions, multiple city strategic plans, several
            municipal comprehensive plans, and a regional housing study — a
            near-constant drumbeat of “we need your input” from every economic-
            and community-development actor at once.
          </li>
          <li>
            <strong>Low public visibility.</strong> PADD is trusted by its
            institutional partners but is not widely known to the general
            public. The community survey confirms it directly:{" "}
            <strong>41% of respondents had never heard of PADD before taking the
            survey</strong>, and only <strong>17% had ever worked with it.</strong>
          </li>
        </ul>

        <div className="callout">
          <span className="callout-label">Why this belongs in the plan, under Goal 4</span>
          <p>
            These conditions are a measurable starting line. Naming them lets
            this CEDS set a baseline it can actually move: rebuild the recurring
            planning culture, raise PADD’s public visibility, reduce duplicated
            civic asks through regional coordination, and hold the organization
            accountable in public. That is the work of{" "}
            <strong>Goal 4: Build Regional Capacity, Accountability &amp;
            Long-Term Economic Resilience</strong>, and the engagement climate
            documented here is its evidence base.
          </p>
        </div>

        <h3>11.7 How Community Input Shaped This CEDS</h3>
        <p>
          The engagement summarized above did more than confirm the plan’s
          direction — it changed it. The table below traces each major input to
          the specific revision it produced.
        </p>
        <SimpleTable head={["What we heard", "Source", "How the CEDS responds"]} rows={INPUT_TO_RESPONSE} />
        <p>
          Read together, these inputs tell a consistent story: the Panhandle’s
          people are committed to the region, clear about its top constraints,
          and impatient for measurable results. This CEDS is built to meet them
          there.
        </p>
      </div>
    </div>
  );
}
